import { useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  IVacationBidPackage,
  IVacationChangeRequest,
  IVacationChangeRequestDecision,
  IVacationChangeWindowListItem,
  IVacationChangeWindowScheduleRow,
  IVacationDate,
  VacationChangeDecision,
  VacationChangeRequestKind,
  VacationChangeRequestStatus,
  VacationChangeWindowState,
  changeRequestKind,
} from "@vmosolutions/oc-interfaces";

import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";

import { VACATION_BID_TEST_ID } from "../testIds";
import { MAX_TIMER_MS, displaySlotId, formatVacationDate, isoToVacationDate, vacationDateToIso } from "../helpers";
import { awardDateRange, awardingSummary, shortBlockLabel, slotDateRange } from "../Awarding/awardingHelpers";
import { AwardingConfirmDialog, IAwardingConfirm } from "../Awarding/AwardingConfirmDialog";
import { BlockCapacityGrid } from "../Awarding/BlockCapacityGrid";
import {
  REQUEST_STATUS_LABEL,
  approveBlockedReason,
  changeCrewName,
  closeAtEndOfDay,
  crewById,
  currentWindow,
  decisionsBlockedReason,
  formatDay,
  holderNamesBySlot,
  isPickUp,
  offerableSlotIds,
  openSlotIds,
  placementLabel,
  requestApplied,
  requestBlockDays,
  requestCounts,
  requestPosition,
  requestsInWindow,
  scheduledWindow,
  unfilledBlocksOf,
  windowDatesLabel,
  windowEditable,
  windowStateLabel,
} from "./changeRequestHelpers";
import { useVacationChangeRequests } from "./useVacationChangeRequests";
import { useBaseTimezone } from "./useBaseTimezone";

export interface IManageAwardsTabProps {
  /** The saved package. */
  pkg: IVacationBidPackage;
  showSlotIdYear: boolean;
  /** The customer's fixed change window calendar, which pre-fills a new window. */
  changeWindowSchedule?: IVacationChangeWindowScheduleRow[];
}

/** The window form: what the scheduler has entered, which may not be saved yet. */
interface IWindowForm {
  opensAt: string | null;
  closesAt: string | null;
  slotIds: Set<string>;
  slotsFrom: IVacationDate | null;
}

const toIso = (value: Date | string | null | undefined): string | null =>
  value ? new Date(value).toISOString() : null;

const sameInstant = (a: string | null, b: Date | string | null | undefined): boolean =>
  (a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);

const sameSlots = (a: Set<string>, b: string[]): boolean => a.size === new Set(b).size && b.every((z) => a.has(z));

const sameDay = (a: IVacationDate | null, b: IVacationDate | null | undefined): boolean =>
  vacationDateToIso(a) === vacationDateToIso(b);

/**
 * The same width for every label, so the window's date fields line up. Important, because the
 * editor's own date field label width is set by a far more specific rule.
 */
const WINDOW_DATE_LABEL_STYLE = { width: "70px !important" };

const APPLIED_HINT = "This change has already been applied. Approve it to finish recording the decision.";

const ManageAwardsTabStyles = (theme: Theme): any => ({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  padding: "10px",
  overflowY: "auto",
  position: "relative",
  "& .manage_header": { display: "flex", alignItems: "baseline", gap: "10px" },
  "& .manage_title": { fontSize: 14, fontWeight: 700 },
  "& .manage_summary": { marginLeft: "auto", fontSize: 12, color: theme.custom.forms.labels },
  "& .manage_open": { color: theme.palette.success.main },
  "& .panel": {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "10px 14px",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.custom.forms.inputBorder}`,
    borderRadius: "3px",
  },
  "& .panel.requests": { borderColor: theme.palette.warning.main },
  "& .panel_header": { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  "& .panel_title": { fontSize: 11, textTransform: "uppercase", color: theme.custom.forms.labels },
  "& .panel.requests .panel_title": { color: theme.palette.warning.main },
  "& .panel_hint": { fontSize: 11, color: theme.custom.forms.labels },
  "& .panel_actions": { marginLeft: "auto", display: "flex", gap: "6px" },
  "& .MuiButtonBase-root.MuiButton-root": { padding: "2px 10px", fontSize: 12, textTransform: "none" },
  "& .window_row": {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "12px 20px",
    padding: "8px 10px",
    border: `1px solid ${theme.custom.forms.inputBorder}`,
    borderRadius: "3px",
    "& .window_heading": { display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 240px", minWidth: 0 },
    "& .window_label": { fontSize: 12, color: theme.palette.success.main },
    "& .window_state": { fontSize: 11, color: theme.custom.forms.labels },
    "& .window_state.refused": { fontSize: 12, color: theme.palette.warning.main },
    // From and To sit one above the other so the pair never wraps apart on a narrow screen.
    "& .window_dates": { display: "flex", flexDirection: "column", gap: "6px" },
    "& .window_actions": { alignSelf: "center" },
  },
  "& .request_card": {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "10px 14px",
    border: `1px solid ${theme.custom.forms.inputBorder}`,
    borderRadius: "3px",
    backgroundColor: theme.custom.timeline.contextListBackground,
  },
  "& .request_head": { display: "flex", alignItems: "center", gap: "8px", fontSize: 12, cursor: "pointer" },
  "& .request_toggle": { fontSize: 20, transition: "transform 150ms" },
  "& .request_toggle.open": { transform: "rotate(180deg)" },
  "& .request_who": { fontSize: 12, "& b": { fontWeight: 700 } },
  "& .request_summary": { fontSize: 12, color: theme.custom.forms.labels },
  "& .request_status": { marginLeft: "auto", height: "20px", fontSize: 11 },
  "& .request_status.pending": { color: theme.palette.warning.main, borderColor: theme.palette.warning.main },
  "& .request_status.approved": { color: theme.palette.success.main, borderColor: theme.palette.success.main },
  "& .request_status.denied": { color: theme.palette.error.main, borderColor: theme.palette.error.main },
  "& .request_note": {
    fontSize: 12,
    padding: "6px 12px",
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
  },
  "& .request_blocks": { display: "flex", alignItems: "flex-end", gap: "14px", flexWrap: "wrap" },
  "& .request_block": {
    flex: 1,
    minWidth: "240px",
    "& .block_label": { fontSize: 11, textTransform: "uppercase", color: theme.custom.forms.labels },
    "& .block_value": {
      fontSize: 13,
      padding: "6px 10px",
      margin: "3px 0",
      border: `1px solid ${theme.custom.forms.inputBorder}`,
      borderRadius: "3px",
      backgroundColor: theme.palette.background.paper,
    },
    "& .block_effect": { fontSize: 11 },
    "&.release .block_value": { borderColor: theme.palette.error.main },
    "&.release .block_effect": { color: theme.palette.error.main },
    "&.take .block_value": { borderColor: theme.palette.success.main },
    "&.take .block_effect": { color: theme.palette.success.main },
    "&.take.full .block_effect": { color: theme.palette.warning.main },
    "&.pickup .block_value": { borderStyle: "dashed" },
    "&.pickup .block_effect": { color: theme.palette.warning.main },
    "&.leftover .block_value": { borderColor: theme.palette.primary.main },
    "&.leftover .block_effect": { color: theme.palette.primary.main },
  },
  "& .request_actions": { display: "flex", gap: "6px", paddingBottom: "18px" },
  "& .request_blocked": { fontSize: 12, color: theme.palette.warning.main },
  "& .request_footnote": { fontSize: 11, color: theme.custom.forms.labels },
  "& .loadingSpinner": { position: "absolute", inset: 0, zIndex: 10 },
});

/** Manage Awards: the package's block availability, its change windows, and the requests crew made in them. */
export const ManageAwardsTab = (props: IManageAwardsTabProps): JSX.Element => {
  const { pkg, showSlotIdYear, changeWindowSchedule } = props;
  const changes = useVacationChangeRequests(pkg._id);
  const { view, loading, busy, load } = changes;
  const timezoneId = useBaseTimezone(pkg.baseAirport);

  useEffect(() => {
    void load();
  }, [load]);

  const [showOpenOnly, setShowOpenOnly] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<IAwardingConfirm | null>(null);
  const [denying, setDenying] = useState<IVacationChangeRequest | null>(null);
  const [denyReason, setDenyReason] = useState<string>("");
  const [chosenWindowId, setChosenWindowId] = useState<string | null>(null);
  // Cards the scheduler opened or closed; any other is open only while it is the next to decide.
  const [toggled, setToggled] = useState<Map<string, boolean>>(() => new Map());
  const setOpen = (id: string, open: boolean): void => setToggled((current) => new Map(current).set(id, open));

  const usage = useMemo(() => view?.usage ?? [], [view]);
  const usageBySlot = useMemo(() => new Map(usage.map((z) => [z.slotId, z])), [usage]);
  const slotsById = useMemo(() => new Map((view?.slots ?? []).map((z) => [z.slotId, z])), [view]);
  const openIds = useMemo(() => openSlotIds(usage), [usage]);
  const namesBySlot = useMemo(() => (view ? holderNamesBySlot(view) : new Map<string, string[]>()), [view]);
  const crew = useMemo(() => crewById(view?.crew ?? []), [view]);
  const summary = useMemo(() => awardingSummary(usage, []), [usage]);

  const working = view ? currentWindow(view.windows) : null;
  const editable = windowEditable(working);

  // A window's state comes from the server, so the view reloads when the window next opens or closes.
  const nextBoundary = working
    ? [working.opensAt, working.closesAt].map((z) => new Date(z).getTime()).find((z) => z > Date.now())
    : undefined;
  useEffect(() => {
    if (nextBoundary === undefined) return undefined;
    const timer = window.setTimeout(
      (): void => {
        void load();
      },
      Math.min(nextBoundary - Date.now() + 2000, MAX_TIMER_MS)
    );
    return () => window.clearTimeout(timer);
  }, [nextBoundary, load]);

  // The form follows the window being worked on, and resets whenever the server's copy of it changes. A new one is
  // filled from the customer's schedule, once the base's zone is known.
  const formKey = view
    ? `${working?._id ?? "new"}|${String(working?.modifiedAt ?? "")}|${working?.state ?? ""}|${timezoneId ?? ""}`
    : "";
  const [syncedFormKey, setSyncedFormKey] = useState<string>("");
  const [form, setForm] = useState<IWindowForm>(() => ({
    opensAt: null,
    closesAt: null,
    slotIds: new Set(),
    slotsFrom: null,
  }));
  if (view && formKey !== syncedFormKey) {
    const planned = working ? null : scheduledWindow(changeWindowSchedule, view.today, timezoneId);
    setSyncedFormKey(formKey);
    setForm(
      working
        ? {
            opensAt: toIso(working.opensAt),
            closesAt: toIso(working.closesAt),
            slotIds: new Set(working.slotIds),
            slotsFrom: working.slotsFrom ?? null,
          }
        : {
            opensAt: planned?.opensAt ?? null,
            closesAt: planned?.closesAt ?? null,
            slotIds: offerableSlotIds(view.slots, view.today, null, timezoneId),
            slotsFrom: planned?.slotsFrom ?? null,
          }
    );
  }

  const dirty = working
    ? !sameInstant(form.opensAt, working.opensAt) ||
      !sameInstant(form.closesAt, working.closesAt) ||
      !sameSlots(form.slotIds, working.slotIds) ||
      !sameDay(form.slotsFrom, working.slotsFrom)
    : !!form.opensAt || !!form.closesAt || !!form.slotsFrom;
  const writable = !!view?.windowsAllowed && !busy;
  const offerable = useMemo(
    () =>
      view
        ? offerableSlotIds(view.slots, view.today, { closesAt: form.closesAt, slotsFrom: form.slotsFrom }, timezoneId)
        : new Set<string>(),
    [view, form.closesAt, form.slotsFrom, timezoneId]
  );
  const offeredIds = editable.slots ? [...form.slotIds].filter((z) => offerable.has(z)) : [...form.slotIds];
  // A block excluded while the window is open stays in it, but is no longer listed or offered.
  const offeredCount = offeredIds.filter((z) => slotsById.has(z)).length;

  const saveWindow = (): void => {
    if (!form.opensAt || !form.closesAt) return;
    if (!working) {
      void changes.createWindow({
        opensAt: new Date(form.opensAt),
        closesAt: new Date(form.closesAt),
        slotIds: offeredIds,
        slotsFrom: form.slotsFrom,
      });
      return;
    }
    if (working.state === VacationChangeWindowState.Open && new Date(form.closesAt).getTime() <= Date.now()) {
      closeWindowNow(working);
      return;
    }
    // Only what changed is sent, so a stale form cannot undo another scheduler's edit.
    void changes.updateWindow(working._id, {
      ...(editable.opens && !sameInstant(form.opensAt, working.opensAt) ? { opensAt: new Date(form.opensAt) } : {}),
      ...(!sameInstant(form.closesAt, working.closesAt) ? { closesAt: new Date(form.closesAt) } : {}),
      ...(editable.slots && !sameSlots(new Set(offeredIds), working.slotIds) ? { slotIds: offeredIds } : {}),
      ...(editable.slots && !sameDay(form.slotsFrom, working.slotsFrom) ? { slotsFrom: form.slotsFrom } : {}),
    });
  };

  const toggleSlot = (slotId: string): void =>
    setForm((current) => {
      const slotIds = new Set(current.slotIds);
      if (slotIds.has(slotId)) slotIds.delete(slotId);
      else slotIds.add(slotId);
      return { ...current, slotIds };
    });

  const shareWindow = (window: IVacationChangeWindowListItem): void => {
    // A block excluded since the window was drafted stays in it, but is not offered to crew.
    const offered = window.slotIds.filter((z) => slotsById.has(z)).length;
    setConfirm({
      title: "Share this change window to crew?",
      lines: [
        `Crew in this package will see ${offered} offered block${offered === 1 ? "" : "s"}` +
          `${window.slotsFrom ? ` from ${formatVacationDate(window.slotsFrom)} on` : ""}, ` +
          `and can ask for one of them from ${windowDatesLabel(window, timezoneId)}: in place of a block they hold, or as ` +
          "a pick-up when they are short of their entitlement. Only offered blocks can be given up or taken.",
        "Once the window opens, only the date it closes can change.",
      ],
      confirmLabel: "Share to Crew",
      onConfirm: () => void changes.shareWindow(window._id),
    });
  };

  const deleteWindow = (window: IVacationChangeWindowListItem): void =>
    setConfirm({
      title: "Delete this change window?",
      lines: [
        window.state === VacationChangeWindowState.Upcoming
          ? "Crew have been told about this window. Deleting it takes it away before it opens."
          : "The draft is removed. Crew never saw it.",
      ],
      confirmLabel: "Delete",
      onConfirm: () => void changes.deleteWindow(window._id),
    });

  const closeWindowNow = (window: IVacationChangeWindowListItem): void =>
    setConfirm({
      title: "Close this change window now?",
      lines: ["Crew can no longer ask for changes or withdraw their requests.", "Its requests can then be decided."],
      confirmLabel: "Close Now",
      onConfirm: () => void changes.updateWindow(window._id, { closesAt: new Date() }),
    });

  // The window the scheduler picked, else the one being worked on, else the newest shared one.
  const windows = view?.windows ?? [];
  const shownWindow =
    windows.find((z) => z._id === chosenWindowId) ??
    working ??
    windows.find((z) => z.state !== VacationChangeWindowState.Draft) ??
    null;
  const requests = view && shownWindow ? requestsInWindow(view.requests, shownWindow._id) : [];
  const blocked = pkg.isDisabled
    ? "This package is disabled, so its requests cannot be decided."
    : decisionsBlockedReason(shownWindow, timezoneId);
  const firstPendingId = requests.find((z) => z.status === VacationChangeRequestStatus.Pending)?._id;

  // A decided card folds away.
  const decide = (request: IVacationChangeRequest, decision: IVacationChangeRequestDecision): Promise<boolean> =>
    changes.decide(String(request._id), decision).then((decided) => {
      if (decided) setOpen(String(request._id), false);
      return decided;
    });

  const approve = (request: IVacationChangeRequest): void => {
    const { take } = requestCounts(request, usageBySlot);
    if (!take?.full || (view && requestApplied(view, request))) {
      void decide(request, { decision: VacationChangeDecision.Approve });
      return;
    }
    setConfirm({
      title: "Approve into a full block?",
      lines: [
        `${displaySlotId(request.takeSlotId, showSlotIdYear)} has ${take.awarded} awarded against ${take.capacity} places.`,
        `Approving takes it to ${take.after}/${take.capacity}, over its capacity.`,
      ],
      confirmLabel: "Approve Anyway",
      onConfirm: () => void decide(request, { decision: VacationChangeDecision.Approve, allowOverCapacity: true }),
    });
  };

  const closeDeny = (): void => {
    setDenying(null);
    setDenyReason("");
  };

  // The dialog stays open until the deny lands, so a refused one keeps its reason.
  const confirmDeny = async (): Promise<void> => {
    if (!denying) return;
    if (await decide(denying, { decision: VacationChangeDecision.Deny, reason: denyReason.trim() })) closeDeny();
  };
  const denyingKeeps =
    !!denying?.releaseSlotId &&
    !!view?.awards.some((z) => String(z.userId) === String(denying.userId) && z.slotId === denying.releaseSlotId);

  // A short block shows its own dates and days rather than its whole slot's.
  const slotLabel = (slotId: string, blockDays?: number): string => {
    const slot = slotsById.get(slotId);
    if (!slot) return displaySlotId(slotId, showSlotIdYear);
    const short = blockDays === undefined ? null : shortBlockLabel({ blockDays }, slot);
    return (
      `${displaySlotId(slotId, showSlotIdYear)} · ` +
      (short && blockDays !== undefined ? `${awardDateRange({ blockDays }, slot)} · ${short}` : slotDateRange(slot))
    );
  };

  const renderRequest = (request: IVacationChangeRequest): JSX.Element => {
    const id = String(request._id);
    const pending = request.status === VacationChangeRequestStatus.Pending;
    const expanded = toggled.get(id) ?? id === firstPendingId;
    const member = crew.get(String(request.userId));
    const name = changeCrewName(crew, String(request.userId));
    const counts = requestCounts(request, usageBySlot);
    const kind = changeRequestKind(request);
    const pickUp = isPickUp(request);
    const release = request.releaseSlotId ? displaySlotId(request.releaseSlotId, showSlotIdYear) : "";
    const take = request.takeSlotId ? displaySlotId(request.takeSlotId, showSlotIdYear) : "";
    const placement = request.leftoverPlacement
      ? placementLabel(request.leftoverPlacement, (slotId) => displaySlotId(slotId, showSlotIdYear))
      : "";
    const days = member?.leftoverDays
      ? `${member.leftoverDays} leftover day${member.leftoverDays === 1 ? "" : "s"}`
      : "";
    // Whether the days sit on the released block now, and so leave it with the award.
    const daysMove =
      !!request.releaseSlotId &&
      !!view?.awards.some(
        (z) => String(z.userId) === String(request.userId) && z.slotId === request.releaseSlotId && z.leftoverDays
      );
    const summary =
      kind === VacationChangeRequestKind.PlaceLeftover
        ? `wants ${days || "their leftover days"} ${placement}`
        : `${pickUp ? `wants to pick up ${take}` : `wants to move ${release} to ${take}`}${
            placement ? ` · leftover days ${placement}` : ""
          }`;
    const unfilled = view ? unfilledBlocksOf(view, String(request.userId)) : null;
    const onlyDeny = view
      ? approveBlockedReason(view, request, (slotId) => displaySlotId(slotId, showSlotIdYear))
      : null;
    const position = requestPosition(requests, request);
    const applied = !!view && requestApplied(view, request);
    const released = view?.awards.find(
      (z) => String(z.userId) === String(request.userId) && z.slotId === request.releaseSlotId
    );
    const takenDays = view ? requestBlockDays(view, request, slotsById) : null;

    return (
      <div key={id} className="request_card" data-testid={VACATION_BID_TEST_ID.changeRequestCard}>
        <div
          className="request_head"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setOpen(id, !expanded)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setOpen(id, !expanded);
            }
          }}
          data-testid={VACATION_BID_TEST_ID.changeRequestToggle}
        >
          <Typography className="request_who">
            <b>{name}</b>
            {` · ${member?.seniority === null || member?.seniority === undefined ? "No seniority" : `Seniority ${member.seniority}`}`}
            {position ? ` · ${position}` : ""}
            {` · requested ${formatDay(request.submittedAt, timezoneId)}`}
          </Typography>
          {!expanded && <Typography className="request_summary">{summary}</Typography>}
          <Chip
            size="small"
            variant="outlined"
            className={`request_status ${request.status}`}
            label={REQUEST_STATUS_LABEL[request.status]}
          />
          <ExpandMoreIcon className={`request_toggle${expanded ? " open" : ""}`} />
        </div>

        {expanded && (
          <>
            {!!request.note && <Typography className="request_note">{`"${request.note}"`}</Typography>}
            <div className="request_blocks">
              {kind === VacationChangeRequestKind.Swap && counts.release && request.releaseSlotId && (
                <div className="request_block release">
                  <Typography className="block_label">{`Release (${pending ? "currently held by" : "given up by"} ${name})`}</Typography>
                  <Typography className="block_value">
                    {`${slotLabel(request.releaseSlotId, request.releaseBlockDays ?? released?.blockDays)}${pending ? ` · currently ${counts.release.awarded}/${counts.release.capacity}` : ""}`}
                  </Typography>
                  {pending && (
                    <Typography className="block_effect">
                      {`Slot returns to pool · ${release} becomes ${counts.release.after}/${counts.release.capacity}`}
                    </Typography>
                  )}
                </div>
              )}
              {pickUp && (
                <div className="request_block pickup">
                  <Typography className="block_label">Pick-up (no block given up)</Typography>
                  <Typography className="block_value">
                    {unfilled === null
                      ? "Unfilled entitlement cannot be counted in blocks"
                      : `${unfilled} unfilled block${unfilled === 1 ? "" : "s"} now`}
                  </Typography>
                </div>
              )}
              {counts.take && request.takeSlotId && (
                <div className={`request_block take${counts.take.full ? " full" : ""}`}>
                  <Typography className="block_label">Assign (crew&apos;s requested block)</Typography>
                  <Typography className="block_value">
                    {`${slotLabel(request.takeSlotId, takenDays?.days)}${
                      pending
                        ? counts.take.full
                          ? " · full"
                          : ` · ${counts.take.capacity - counts.take.awarded} slot${counts.take.capacity - counts.take.awarded === 1 ? "" : "s"} open`
                        : ""
                    }`}
                  </Typography>
                  {pending && (
                    <Typography className="block_effect">
                      {`${counts.take.full ? "Over capacity" : "Slot consumed"} · ${take} becomes ${counts.take.after}/${counts.take.capacity}`}
                    </Typography>
                  )}
                </div>
              )}
              {request.leftoverPlacement && (
                <div className="request_block leftover">
                  <Typography className="block_label">
                    {`Leftover days (${daysMove ? `move off ${release}` : "unplaced now"})`}
                  </Typography>
                  <Typography className="block_value">
                    {`${days ? `${days} ` : ""}${placementLabel(request.leftoverPlacement, slotLabel)}`}
                  </Typography>
                  {pending && <Typography className="block_effect">Placed on approval</Typography>}
                </div>
              )}
              {pending && (
                <div className="request_actions">
                  <Tooltip title={blocked ?? onlyDeny ?? ""}>
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        disabled={!!blocked || !!onlyDeny || busy}
                        onClick={() => approve(request)}
                        data-testid={VACATION_BID_TEST_ID.approveChangeRequestButton}
                      >
                        {applied ? "Finish Approving" : "Approve Change"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={blocked ?? (applied ? APPLIED_HINT : "")}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!!blocked || applied || busy}
                        onClick={() => setDenying(request)}
                        data-testid={VACATION_BID_TEST_ID.denyChangeRequestButton}
                      >
                        Deny
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
            {(onlyDeny || applied) && <Typography className="request_blocked">{onlyDeny ?? APPLIED_HINT}</Typography>}
            <Typography className="request_footnote">
              {pending
                ? kind === VacationChangeRequestKind.PlaceLeftover
                  ? "Only the leftover days move. No block's count changes."
                  : pickUp
                    ? "The block's count goes up on approval."
                    : "Both counts update together on approval."
                : `${REQUEST_STATUS_LABEL[request.status]} ${formatDay(request.resolvedAt, timezoneId)}${
                    request.denyReason ? ` · Reason given to crew: ${request.denyReason}` : ""
                  }`}
            </Typography>
          </>
        )}
      </div>
    );
  };

  return (
    <Box sx={ManageAwardsTabStyles} data-testid={VACATION_BID_TEST_ID.manageAwardsTab}>
      <SpinnerProgress className="loadingSpinner" loading={loading && !view} message="loading change requests" />

      {!view && !loading && (
        <Typography variant="body2">
          The package&apos;s change requests could not be loaded.{" "}
          <Button size="small" onClick={() => void load()}>
            Try again
          </Button>
        </Typography>
      )}

      {view && (
        <>
          <div className="manage_header">
            <Typography className="manage_title">Manage Awards</Typography>
            <Typography className="manage_summary" data-testid={VACATION_BID_TEST_ID.manageAwardsSummary}>
              {`${summary.awarded} of ${summary.capacity} slots awarded · `}
              <span className="manage_open">{`${summary.remaining} open`}</span>
            </Typography>
            <Button size="small" variant="outlined" disabled={loading} onClick={() => void load()}>
              Refresh
            </Button>
          </div>

          <div className="panel availability">
            <div className="panel_header">
              <Typography className="panel_title">Block availability</Typography>
              <div className="panel_actions">
                {working && editable.share && (
                  <Tooltip title={dirty ? "Save the window before sharing it" : ""}>
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!writable || dirty || !working.slotIds.length}
                        onClick={() => shareWindow(working)}
                        data-testid={VACATION_BID_TEST_ID.shareChangeWindowButton}
                      >
                        Share to Crew
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="window_row">
              <div className="window_heading">
                <Typography className="window_label">Crew change request window</Typography>
                <Typography
                  className={`window_state${view.windowsAllowed ? "" : " refused"}`}
                  data-testid={VACATION_BID_TEST_ID.changeWindowState}
                >
                  {!view.windowsAllowed
                    ? view.windowsRefusedReason
                    : working
                      ? windowStateLabel(working, timezoneId)
                      : "Crew can see open blocks and ask for changes only within this window, once it is shared."}
                </Typography>
              </div>
              <div className="window_dates">
                <DateTimeField
                  key={`opens-${syncedFormKey}`}
                  className="field datetimefield"
                  name="changeWindowOpensAt"
                  label="From"
                  labelStyle={WINDOW_DATE_LABEL_STYLE}
                  value={form.opensAt ?? ""}
                  disabled={!writable || !editable.opens}
                  showAirportTime={pkg.baseAirport}
                  localizeDisplay={!!pkg.baseAirport}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, opensAt: value ? toIso(String(value)) : null }))
                  }
                  data-testid={VACATION_BID_TEST_ID.changeWindowOpens}
                />
                <DateTimeField
                  key={`closes-${syncedFormKey}`}
                  className="field datetimefield"
                  name="changeWindowClosesAt"
                  label="To"
                  labelStyle={WINDOW_DATE_LABEL_STYLE}
                  value={form.closesAt ?? ""}
                  disabled={!writable || !editable.closes}
                  showAirportTime={pkg.baseAirport}
                  localizeDisplay={!!pkg.baseAirport}
                  onChange={(value) =>
                    setForm((current) => ({
                      ...current,
                      closesAt: value
                        ? closeAtEndOfDay(current.closesAt, new Date(value).toISOString(), timezoneId)
                        : null,
                    }))
                  }
                  data-testid={VACATION_BID_TEST_ID.changeWindowCloses}
                />
                <DateTimeField
                  key={`slots-from-${syncedFormKey}`}
                  className="field datetimefield"
                  name="changeWindowSlotsFrom"
                  label="Slots from"
                  labelStyle={WINDOW_DATE_LABEL_STYLE}
                  value={vacationDateToIso(form.slotsFrom)}
                  hideTimeControl={true}
                  disabled={!writable || !editable.slots}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, slotsFrom: value ? isoToVacationDate(String(value)) : null }))
                  }
                  data-testid={VACATION_BID_TEST_ID.changeWindowSlotsFrom}
                />
              </div>
              <div className="panel_actions window_actions">
                {(editable.opens || editable.closes) && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={!writable || !dirty || !form.opensAt || !form.closesAt}
                    onClick={saveWindow}
                    data-testid={VACATION_BID_TEST_ID.saveChangeWindowButton}
                  >
                    {working ? "Save Window" : "Save Draft"}
                  </Button>
                )}
                {working?.state === VacationChangeWindowState.Open && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!writable}
                    onClick={() => closeWindowNow(working)}
                    data-testid={VACATION_BID_TEST_ID.closeChangeWindowButton}
                  >
                    Close Now
                  </Button>
                )}
                {working && editable.delete && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={!writable}
                    onClick={() => deleteWindow(working)}
                    data-testid={VACATION_BID_TEST_ID.deleteChangeWindowButton}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>

            <BlockCapacityGrid
              title="Offered blocks"
              hint={
                editable.slots && view.windowsAllowed
                  ? `${offeredCount} of ${view.slots.length} blocks offered · click a block to grey it out of the window (crew can't give it up or take it), or bring it back`
                  : `${offeredCount} of ${view.slots.length} blocks offered`
              }
              slots={view.slots}
              usageBySlot={usageBySlot}
              crewNamesBySlot={namesBySlot}
              showSlotIdYear={showSlotIdYear}
              onlySlotIds={showOpenOnly ? openIds : null}
              onlyToggleLabel={showOpenOnly ? "Show all blocks" : `Show only open blocks (${openIds.size})`}
              onlyToggleDisabled={false}
              onToggleOnly={() => setShowOpenOnly((current) => !current)}
              selectedSlotIds={form.slotIds}
              onToggleSlot={writable && editable.slots ? toggleSlot : undefined}
              locked={
                editable.slots
                  ? {
                      slotIds: new Set(view.slots.filter((z) => !offerable.has(z.slotId)).map((z) => z.slotId)),
                      reason: form.slotsFrom
                        ? "Starts on or before the day the window closes, or before Slots from, so it cannot be offered"
                        : "Starts on or before the day the window closes, so it cannot be offered",
                    }
                  : undefined
              }
            />
          </div>

          <div className="panel requests">
            <div className="panel_header">
              <Typography className="panel_title">{`Crew requests (${requests.length})`}</Typography>
              <Typography className="panel_hint">
                {shownWindow
                  ? `submitted during the change request window · ${windowDatesLabel(shownWindow, timezoneId)}`
                  : "no change window yet"}
              </Typography>
              {windows.length > 1 && (
                <TextField
                  select
                  size="small"
                  className="panel_actions"
                  value={shownWindow?._id ?? ""}
                  onChange={(event) => setChosenWindowId(event.target.value)}
                  inputProps={{ "data-testid": VACATION_BID_TEST_ID.changeWindowSelect }}
                >
                  {windows.map((window) => (
                    <MenuItem key={window._id} value={window._id}>
                      {windowDatesLabel(window, timezoneId)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </div>
            {!!requests.length && blocked && <Typography className="panel_hint">{blocked}</Typography>}
            {!requests.length && (
              <Typography className="panel_hint">
                {shownWindow
                  ? "No crew have asked for a change in this window."
                  : "Requests appear here once a window is shared and crew ask."}
              </Typography>
            )}
            {requests.map(renderRequest)}
          </div>
        </>
      )}

      <AwardingConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />

      <Dialog open={!!denying} onClose={closeDeny} data-testid={VACATION_BID_TEST_ID.denyChangeRequestDialog}>
        <DialogTitle>Deny this change request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ marginBottom: "10px" }}>
            {denying
              ? `${changeCrewName(crew, String(denying.userId))} ` +
                (!denying.takeSlotId
                  ? "keeps their leftover days where they are. "
                  : denyingKeeps && denying.releaseSlotId
                    ? `keeps ${displaySlotId(denying.releaseSlotId, showSlotIdYear)}. `
                    : `does not get ${displaySlotId(denying.takeSlotId, showSlotIdYear)}. `) +
                "Any reason you give is shown to them."
              : ""}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            label="Reason (optional)"
            // The theme's default outline barely shows on a dialog, so it gets the request cards' input border.
            sx={(theme: Theme) => ({
              marginTop: "6px",
              "& .MuiOutlinedInput-root": { backgroundColor: theme.palette.background.paper },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.custom.forms.inputBorder },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.custom.forms.labels,
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
            })}
            value={denyReason}
            onChange={(event) => setDenyReason(event.target.value.slice(0, 500))}
            inputProps={{ "data-testid": VACATION_BID_TEST_ID.denyChangeRequestReason, maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" variant="contained" color="secondary" onClick={closeDeny}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={busy}
            onClick={() => void confirmDeny()}
            data-testid={VACATION_BID_TEST_ID.denyChangeRequestConfirmButton}
          >
            Deny
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageAwardsTab;
